import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FinanceCustomerPicker } from '../components/FinanceCustomerPicker';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { BatchDetailDto, BatchDto, DemoCopyResultDto, DemoResetResultDto, OrderListItemDto, ShopDeleteCheckDto, ShopDto, ShopWorkViewDto } from '@thebb/shared';
import { del, get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';
import { statusLabel, statusTone } from './Orders';
import { BatchTable, NewBatchModal } from './Batches';
import { ShopShippingRates } from './Shipping';

const shopStatusLabel: Record<string, string> = { not_started: 'Ikke startet', ongoing: 'I gang', all_packed: 'Alt pakket', all_shipped: 'Alt sendt', archived: 'Arkiveret' };
const typeLabel: Record<string, string> = { regular: 'Åben shop', standard: 'Åben shop', standard_v2: 'Åben shop', login: 'Login-shop', gift: 'Gaveshop', occasion: 'Lejlighedsshop' };
const flowLabel: Record<string, string> = { continuous: 'Løbende', batch: 'Efter lukning' };

function FlowBadge({ shop }: { shop: ShopDto }) {
  return <Badge tone={shop.flow === 'batch' ? 'amber' : 'blue'}>{flowLabel[shop.flow]}{shop.flowOverride ? ' (manuelt)' : ''}</Badge>;
}

/**
 * Shops as 3x knows them: the kind (type and business area) decides how the warehouse works the
 * orders. Continuous shops release one order at a time as they come; gift shops are worked after
 * they close, in batches.
 */
export function ShopsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [flow, setFlow] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [importId, setImportId] = useState('');
  const [error, setError] = useState<unknown>(null);
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (flow) params.set('flow', flow);
  if (showArchived) params.set('includeArchived', 'true');
  const shops = useQuery({ queryKey: ['shops', params.toString()], queryFn: () => get<ShopDto[]>(`/shops?${params}`), refetchInterval: 30_000 });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['shops'] }); void qc.invalidateQueries({ queryKey: ['orders'] }); };
  const importShop = useMutation({
    mutationFn: () => post<{ fetched: number; created: number; updated: number; skipped: number; errors: number }>('/x3/import-shop', { externalShopId: Number(importId) }),
    onSuccess: () => { setImportId(''); invalidate(); },
    onError: setError,
  });
  const syncAll = useMutation({ mutationFn: () => post<{ inX3: number; updated: number }>('/shops/sync-all'), onSuccess: invalidate, onError: setError });

  return (
    <div>
      <PageTitle
        title="Shops"
        actions={
          <>
            <input className="input w-48" placeholder="Søg" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="input w-40" value={flow} onChange={(e) => setFlow(e.target.value)}>
              <option value="">Alle</option>
              <option value="continuous">Løbende ordrer</option>
              <option value="batch">Efter lukning</option>
            </select>
            <label className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Vis arkiverede
            </label>
            {can('shops.import') && (
              <>
                <button className="btn" disabled={syncAll.isPending} onClick={() => syncAll.mutate()}>{syncAll.isPending ? 'Henter…' : 'Opdatér fra 3x'}</button>
                <input className="input w-32" placeholder="3x shop-id" value={importId} onChange={(e) => setImportId(e.target.value)} />
                <button className="btn" disabled={!/^\d+$/.test(importId) || importShop.isPending} onClick={() => importShop.mutate()}>
                  {importShop.isPending ? 'Importerer…' : 'Importér ordrer'}
                </button>
              </>
            )}
          </>
        }
      />
      <ErrorBox error={error} />
      {importShop.data && (
        <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          Import: {importShop.data.fetched} hentet, {importShop.data.created} nye, {importShop.data.updated} opdateret, {importShop.data.skipped} sprunget over, {importShop.data.errors} fejl.
        </div>
      )}
      {syncAll.data && <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{syncAll.data.updated} shops opdateret fra 3x ({syncAll.data.inX3} i 3x).</div>}
      {shops.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr>
              <th>Shop</th>
              <th>3x</th>
              <th>Slags</th>
              <th>Arbejdsgang</th>
              <th>Åben</th>
              <th>Lukker</th>
              <th className="text-right">Ordrer</th>
              <th className="text-right">Åbne</th>
              <th>Levering</th>
              <th>Sidste ordre</th>
            </tr>
          </thead>
          <tbody>
            {shops.data?.map((s) => (
              <tr key={s.id} className={s.x3Archived ? 'text-zinc-400' : ''}>
                <td>
                  <Link to="/shops/$id" params={{ id: s.id }} className="font-medium text-blue-700 hover:underline">{s.name}</Link>
                  {s.giftuniverse && <Badge tone="amber">GU</Badge>}
                  {s.demo && <Badge tone="blue">DEMO</Badge>}
                </td>
                <td className="text-zinc-500">{s.demo ? '' : s.externalId}</td>
                <td>{s.type ? <>{typeLabel[s.type] ?? s.type}{s.businessAreaLabel && <div className="text-xs text-zinc-500">{s.businessAreaLabel}</div>}</> : <span className="text-zinc-400" title="Ikke hentet fra 3x's shop-API endnu">ukendt</span>}</td>
                <td><FlowBadge shop={s} /></td>
                <td>{s.opensAt ?? ''}</td>
                <td>{s.closesAt ?? ''}</td>
                <td className="text-right tabular-nums">{s.orderCount}</td>
                <td className="text-right tabular-nums">{s.openOrderCount}</td>
                <td>{s.deliveryDate ?? ''}{s.deliveryWeek ? ` (uge ${s.deliveryWeek})` : ''}</td>
                <td>{s.lastOrderAt?.slice(0, 10) ?? '–'}</td>
              </tr>
            ))}
            {shops.data?.length === 0 && (
              <tr>
                <td colSpan={10} className="text-zinc-500">Ingen shops endnu. Slå 3x-feedet til under 3x-feed, eller importér en shop med dens 3x-id.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ShopDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [newBatch, setNewBatch] = useState(false);
  const [demoCopy, setDemoCopy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const batches = useQuery({ queryKey: ['batches', 'shop', id], queryFn: () => get<{ items: BatchDto[] }>(`/batches?shopId=${id}&perPage=100`), refetchInterval: 30_000 });
  const work = useQuery({ queryKey: ['shop-work', id], queryFn: () => get<ShopWorkViewDto>(`/shops/${id}/work`), refetchInterval: 30_000 });
  const settings = useQuery({ queryKey: ['shop-settings', id], queryFn: () => get<Record<string, unknown>>(`/shops/${id}/settings`), enabled: showSettings });
  const recent = useQuery({ queryKey: ['orders', 'shop', id], queryFn: () => get<{ items: OrderListItemDto[]; total: number }>(`/orders?shopId=${id}&perPage=25`), refetchInterval: 30_000 });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['shop-work', id] }); void qc.invalidateQueries({ queryKey: ['shops'] }); };
  const update = useMutation({ mutationFn: (v: Record<string, unknown>) => patch(`/shops/${id}`, v), onSuccess: invalidate, onError: setError });
  const sync = useMutation({ mutationFn: () => post(`/shops/${id}/sync`), onSuccess: () => { invalidate(); void qc.invalidateQueries({ queryKey: ['shop-settings', id] }); }, onError: setError });
  const demoReset = useMutation({
    mutationFn: () => post<DemoResetResultDto>(`/shops/${id}/demo/reset`),
    onSuccess: () => { invalidate(); void qc.invalidateQueries({ queryKey: ['orders', 'shop', id] }); void qc.invalidateQueries({ queryKey: ['batches', 'shop', id] }); },
    onError: setError,
  });

  if (work.isLoading || !work.data) return <Spinner />;
  const { shop, byStatus, rows, packableOrders, unpackableOrders } = work.data;
  const total = rows.reduce((s, r) => s + r.ordered, 0);
  const shipped = rows.reduce((s, r) => s + r.shipped, 0);

  return (
    <div className="space-y-4">
      <PageTitle
        title={shop.name}
        actions={
          <>
            <Link to="/shops" className="btn">Tilbage</Link>
            <Link to="/shops/$id/storefront" params={{ id }} className="btn">Webshop</Link>
            <button className="btn" onClick={() => setShowSettings(!showSettings)}>{showSettings ? 'Skjul 3x-indstillinger' : '3x-indstillinger'}</button>
            {can('shops.import') && !shop.demo && <button className="btn" disabled={sync.isPending} onClick={() => sync.mutate()}>{sync.isPending ? 'Henter…' : 'Opdatér fra 3x'}</button>}
            {can('shops.storefront') && !shop.demo && <button className="btn" onClick={() => setDemoCopy(true)}>Lav demokopi</button>}
            {can('shops.storefront') && shop.demo && <button className="btn" disabled={demoReset.isPending} onClick={() => { if (confirm('Annullér demoshoppens åbne ordrer og fyld demolageret op igen?')) demoReset.mutate(); }}>{demoReset.isPending ? 'Nulstiller…' : 'Nulstil demo'}</button>}
            {can('shops.manage') && <button className="btn btn-danger" onClick={() => setDeleting(true)}>Slet shop</button>}
            {shop.flow === 'batch' && can('batches.create') && <button className="btn btn-primary" onClick={() => setNewBatch(true)}>Ny pakkebatch</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      {shop.demo && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <b>Demoshop.</b> Ordrer herfra plukkes fra lageret DEMO og forlader aldrig systemet: ingen mails til kunder, ingen kortbetaling, fragt og Dinero kun som testkørsel, ingen automatisk udskrift på lagerets printere.
          {shop.demoSourceShopId && <> Kopi af <Link to="/shops/$id" params={{ id: shop.demoSourceShopId }} className="underline">den rigtige shop</Link>.</>}
          {demoReset.data && <div className="mt-1">Nulstillet: {demoReset.data.cancelledOrders} ordrer annulleret, {demoReset.data.toppedUp} varer fyldt op.</div>}
        </div>
      )}
      {demoCopy && <DemoCopyModal shop={shop} onClose={() => setDemoCopy(false)} />}
      {deleting && <DeleteShopModal shop={shop} onClose={() => setDeleting(false)} />}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <FlowBadge shop={shop} />
        {shop.type ? <Badge>{typeLabel[shop.type] ?? shop.type}</Badge> : <Badge tone="amber">slags ukendt (ikke synkroniseret)</Badge>}
        {shop.businessAreaLabel && <span className="text-zinc-600">{shop.businessAreaLabel}</span>}
        {shop.giftuniverse && <Badge tone="amber">Gaveuniverset{shop.continuousDelivery ? ' · løbende levering' : ''}</Badge>}
        {shop.x3Archived && <Badge tone="red">arkiveret i 3x</Badge>}
        <span className="text-zinc-500">{shop.demo ? 'Demokopi' : `3x-id ${shop.externalId}`}{shop.customerNumber ? ` · kundenr. ${shop.customerNumber}` : ''}{shop.syncedAt ? ` · hentet ${fmtTime(shop.syncedAt)}` : ''}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="card text-sm">
          <div className="label">Overblik</div>
          <div>Åben {shop.opensAt ?? '?'} → lukker {shop.closesAt ?? '?'}</div>
          <div>Ordrer: {shop.orderCount} ({shop.openOrderCount} åbne)</div>
          {shop.flow === 'batch' && <div>Kan pakkes nu: <b>{packableOrders}</b> · kan ikke: <b>{unpackableOrders}</b></div>}
          <div>Stk. i alt: {total} · sendt {shipped}</div>
          {(shop.deliveryDate || shop.deliveryWeek) && <div>Levering {shop.deliveryDate ?? ''}{shop.deliveryWeek ? ` (uge ${shop.deliveryWeek})` : ''}</div>}
          <div>Første/sidste ordre: {shop.firstOrderAt?.slice(0, 10) ?? '–'} / {shop.lastOrderAt?.slice(0, 10) ?? '–'}</div>
        </div>
        <div className="card text-sm">
          <div className="label">Ordrer pr. status</div>
          {Object.entries(byStatus).map(([s, n]) => (
            <div key={s}>{statusLabel[s] ?? s}: {n}</div>
          ))}
        </div>
        <div className="card space-y-2 text-sm">
          {can('shops.manage') ? (
            <FinanceCustomerPicker label="Faktureres til" value={shop.financeCustomerNumber} onChange={(financeCustomerNumber) => update.mutate({ financeCustomerNumber })} />
          ) : (
            <div><span className="label">Faktureres til</span>{shop.financeCustomerNumber ?? shop.customerNumber ?? '–'}</div>
          )}
          <p className="text-xs text-muted">
            Shoppens fakturaordrer og shopfakturaen går til denne kunde under Økonomi
            {shop.customerNumber ? `; uden valg bruges kundenummeret fra 3x (${shop.customerNumber})` : ''}. En kunde med sin egen Økonomi-kunde under Kunder går forud.
          </p>
          <div className="label pt-2">Arbejdsgang</div>
          <div className="flex items-center gap-2">
            <select className="input" value={shop.flowOverride ?? ''} disabled={!can('shops.manage')} onChange={(e) => update.mutate({ flowOverride: e.target.value || null })}>
              <option value="">Efter reglen ({flowLabel[shop.flow]})</option>
              <option value="continuous">Løbende: én ordre ad gangen</option>
              <option value="batch">Efter lukning: i batches</option>
            </select>
          </div>
          {shop.flow === 'continuous' && (
            <label className="flex items-center gap-2"><input type="checkbox" checked={shop.autoRelease} disabled={!can('shops.manage')} onChange={(e) => update.mutate({ autoRelease: e.target.checked })} /> Frigiv betalbare ordrer til pluk automatisk (slået fra indtil lageret er klar)</label>
          )}
          {shop.flow === 'batch' && (
            <>
              <div className="flex items-center gap-2">
                Prio
                <input className="input w-20" type="number" min={0} max={100} defaultValue={shop.priority} disabled={!can('shops.manage')} onBlur={(e) => Number(e.target.value) !== shop.priority && update.mutate({ priority: Number(e.target.value) })} />
              </div>
              <div className="flex items-center gap-2">
                Forsendelsesdato
                <input className="input w-40" type="date" defaultValue={shop.shipmentDate ?? ''} disabled={!can('shops.manage')} onBlur={(e) => (e.target.value || null) !== shop.shipmentDate && update.mutate({ shipmentDate: e.target.value || null })} />
              </div>
              <div className="flex items-center gap-2">
                Status
                <select className="input" value={shop.status} disabled={!can('shops.manage')} onChange={(e) => update.mutate({ status: e.target.value })}>
                  {Object.entries(shopStatusLabel).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="card text-sm">
          <div className="label">Pakkeinstruktioner</div>
          <textarea className="input" rows={4} defaultValue={shop.packingInstructions ?? ''} disabled={!can('shops.manage')} onBlur={(e) => e.target.value !== (shop.packingInstructions ?? '') && update.mutate({ packingInstructions: e.target.value || null })} />
        </div>
      </div>

      {showSettings && (
        <div className="card text-sm">
          <div className="label">Indstillinger i 3x (spejl)</div>
          {settings.data && Object.keys(settings.data).length > 0 ? (
            <div className="grid gap-x-6 gap-y-1 md:grid-cols-3">
              {Object.entries(settings.data).filter(([, v]) => v !== null && v !== '' && v !== false).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-zinc-100 py-0.5"><span className="text-zinc-500">{k}</span><span className="truncate text-right">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></div>
              ))}
            </div>
          ) : (
            <div className="text-zinc-500">Ikke hentet endnu. Tryk "Opdatér fra 3x" (kræver shop-token under 3x-feed).</div>
          )}
        </div>
      )}

      {shop.flow === 'continuous' ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-zinc-500">Seneste ordrer</h2>
          <table className="table card p-0">
            <thead><tr><th>Nr.</th><th>Kunde</th><th>Status</th><th>Betaling</th><th className="text-right">Total</th><th>Dato</th></tr></thead>
            <tbody>
              {recent.data?.items.map((o) => (
                <tr key={o.id}>
                  <td><Link to="/orders/$id" params={{ id: o.id }} className="font-medium text-blue-700 hover:underline">{o.number}</Link></td>
                  <td>{o.customerName ?? '–'}</td>
                  <td><Badge tone={statusTone(o.status)}>{statusLabel[o.status] ?? o.status}</Badge>{o.needsCheckReason && <div className="text-xs text-red-700">{o.needsCheckReason}</div>}</td>
                  <td>{o.paymentProvider}</td>
                  <td className="text-right tabular-nums">{(o.totalMinor / 100).toFixed(2)} {o.currency ?? ''}</td>
                  <td>{o.placedAt?.slice(0, 10) ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-sm text-zinc-500">Løbende shop: ordrerne frigives én ad gangen{shop.autoRelease ? ' automatisk når de er betalbare' : ' med "Frigiv til pluk" på ordren'} og går til <Link to="/pick" className="text-blue-700 hover:underline">Pluk</Link>.</p>
        </section>
      ) : (
        <>
          <div className="card p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Vare</th>
                  <th className="text-right">Bestilt</th>
                  <th className="text-right">Sendt</th>
                  <th className="text-right">Åben</th>
                  <th className="text-right">Disponibel</th>
                  <th className="text-right">Kan pakkes</th>
                  <th>Fremdrift</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sku} className={r.variantId ? '' : 'bg-red-50'}>
                    <td className="font-mono text-xs">{r.sku}{!r.variantId && <div className="text-red-700">ukendt SKU</div>}</td>
                    <td>{r.title}</td>
                    <td className="text-right tabular-nums">{r.ordered}</td>
                    <td className="text-right tabular-nums">{r.shipped}</td>
                    <td className="text-right tabular-nums">{r.open}</td>
                    <td className="text-right tabular-nums">{r.available}</td>
                    <td className={`text-right tabular-nums ${r.packable < r.open ? 'text-danger' : ''}`}>{r.packable}</td>
                    <td className="w-48">
                      <div className="h-2 w-full rounded bg-zinc-100">
                        <div className="h-2 rounded bg-accent" style={{ width: `${r.ordered ? Math.round((r.shipped / r.ordered) * 100) : 0}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">Pakkebatches</h2>
            <BatchTable items={batches.data?.items ?? []} />
          </section>
          <p className="text-sm text-muted">Gaveshop: arbejdes efter lukning {shop.closesAt ?? ''} i pakkebatches. <Link to="/orders" search={{}} className="text-primary hover:underline">Se alle ordrer</Link>.</p>
          {newBatch && <NewBatchModal shopId={id} onClose={() => setNewBatch(false)} onCreated={(b: BatchDetailDto) => { setNewBatch(false); invalidate(); void qc.invalidateQueries({ queryKey: ['batches'] }); void qc.invalidateQueries({ queryKey: ['orders'] }); window.location.assign(`/batches/${b.id}`); }} onError={setError} />}
        </>
      )}
      <ShopShippingRates shopId={id} />
    </div>
  );
}

/** docs/design/12 section 6: a copy of the shop for showing prospects, with its own logins and the DEMO warehouse. */
function DeleteShopModal({ shop, onClose }: { shop: ShopDto; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const check = useQuery({ queryKey: ['shop-delete-check', shop.id], queryFn: () => get<ShopDeleteCheckDto>(`/shops/${shop.id}/delete-check`), staleTime: 0 });
  const remove = useMutation({
    mutationFn: () => del(`/shops/${shop.id}`),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: ['shop-work', shop.id] });
      void qc.invalidateQueries({ queryKey: ['shops'] });
      await navigate({ to: '/shops' });
    },
    onError: setError,
  });
  const c = check.data;
  return (
    <Modal title={`Slet ${shop.name}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <ErrorBox error={check.error ?? error} />
        {!c ? (
          check.isLoading && <Spinner />
        ) : !c.deletable ? (
          <>
            <p><b>{shop.name}</b> kan ikke slettes, fordi den har {c.blockers.length > 1 ? `${c.blockers.slice(0, -1).join(', ')} og ${c.blockers[c.blockers.length - 1]}` : c.blockers[0]}.</p>
            <p>Arkivér den i stedet: sæt Status til Arkiveret på shoppens side. Så forsvinder den fra listen, og intet går tabt.</p>
            <div className="flex justify-end"><button className="btn" onClick={onClose}>Luk</button></div>
          </>
        ) : (
          <>
            <p>Slet <b>{shop.name}</b>{shop.demo ? ' (demoshop)' : ''} for altid? Det kan ikke fortrydes.</p>
            <p>Med shoppen forsvinder {c.removes.customers} {c.removes.customers === 1 ? 'kunde' : 'kunder'} og logins, {c.removes.products} {c.removes.products === 1 ? 'vare' : 'varer'} i sortimentet, {c.removes.pages} {c.removes.pages === 1 ? 'side' : 'sider'} samt slides, rabatter, leveringsadresser og fragtpriser.{!shop.demo && shop.externalId ? ' Intet slettes i 3x; kommer der en ordre til shoppen fra 3x igen, oprettes den på ny.' : ''}</p>
            {c.removes.domains.length > 0 && <p>Domænet {c.removes.domains.join(', ')} kobles fra, og serveren fjerner dets certifikat inden for et minut.</p>}
            <div className="flex justify-end gap-2">
              <button className="btn" onClick={onClose}>Fortryd</button>
              <button className="btn btn-danger" disabled={remove.isPending} onClick={() => remove.mutate()}>{remove.isPending ? 'Sletter…' : 'Slet shop'}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function DemoCopyModal({ shop, onClose }: { shop: ShopDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(`${shop.name} (demo)`);
  const [slug, setSlug] = useState(shop.slug ? `${shop.slug}-demo` : '');
  const [host, setHost] = useState('');
  const [mailTo, setMailTo] = useState('');
  const copy = useMutation({
    mutationFn: () => post<DemoCopyResultDto>(`/shops/${shop.id}/demo/copy`, { name: name.trim(), slug: slug.trim() || undefined, host: host.trim() || undefined, mailTo: mailTo.trim() || null, logins: 2 }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['shops'] }),
  });
  const r = copy.data;
  return (
    <Modal title="Lav demokopi" onClose={onClose}>
      {r ? (
        <div className="space-y-3 text-sm">
          <p>Demoshoppen <b>{r.name}</b> er klar på <a href={r.url} target="_blank" rel="noreferrer" className="text-blue-700 underline">{r.url}</a>. Lageret DEMO er fyldt op med shoppens varer.</p>
          <div>
            <div className="label">Demo-logins (adgangskoden vises kun nu)</div>
            <table className="table">
              <tbody>
                {r.logins.map((l) => <tr key={l.email ?? l.giftCode}><td className="font-mono text-xs">{l.email ?? l.giftCode}</td><td className="font-mono text-xs">{l.password}</td></tr>)}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Link to="/shops/$id" params={{ id: r.shopId }} className="btn btn-primary" onClick={onClose}>Åbn demoshoppen</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-muted">Kopierer webshoppen (tema, sider, sortiment, priser, leveringsadresser og fragtpriser) uden kunder og ordrer. Ordrer i kopien forlader aldrig systemet.</p>
          <label className="block"><span className="label">Navn</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block"><span className="label">Adresse (/s/…)</span><input className="input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="swisspearl-demo" /></label>
          <label className="block"><span className="label">Domæne (valgfrit)</span><input className="input" value={host} onChange={(e) => setHost(e.target.value)} placeholder="swisspearl-demo.samsinnovation.dev" /><span className="text-xs text-muted">Et domæne, der hører til den rigtige shop, flyttes til kopien.</span></label>
          <label className="block"><span className="label">Demomails til (valgfrit)</span><input className="input" value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="dig@borgbigum.dk" /><span className="text-xs text-muted">Tom: mails bliver i udbakken som demo og kan ses på ordren.</span></label>
          <ErrorBox error={copy.error} />
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={onClose}>Annullér</button>
            <button className="btn btn-primary" disabled={!name.trim() || copy.isPending} onClick={() => copy.mutate()}>{copy.isPending ? 'Kopierer…' : 'Lav demokopi'}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
